import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './emailBroadcastManagement.css';

const endpoint = '/api/admin/email-broadcasts';

const getError = error => error.response?.data?.message || '操作失敗，請稍後重試。';

export default function EmailBroadcastManagement() {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [audienceResponse, historyResponse] = await Promise.all([
        axios.get(`${endpoint}/audience`),
        axios.get(endpoint),
      ]);
      setAudience(audienceResponse.data.count);
      setHistory(historyResponse.data);
    } catch (error) {
      setMessage({ error: true, text: getError(error) });
    }
  }, []);

  useEffect(() => {
    refresh();
    const timer = window.setInterval(refresh, 5000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const valid = subject.trim().length >= 3 && subject.trim().length <= 200 &&
    !/[\r\n]/.test(subject) && body.trim().length >= 10 && body.trim().length <= 20000;
  const active = history.some(item => !item.completedAt && !item.isCanceled);
  const payload = { subject: subject.trim(), body: body.trim() };

  const sendTest = async () => {
    setBusy('test'); setMessage(null);
    try {
      const { data } = await axios.post(`${endpoint}/test`, payload);
      setMessage({ error: false, text: data.message });
    } catch (error) { setMessage({ error: true, text: getError(error) }); }
    finally { setBusy(''); }
  };

  const start = async () => {
    if (audience == null) return;
    if (!window.confirm(`確定把「${subject.trim()}」發送給 ${audience} 位未刪除的註冊用戶嗎？開始後已發出的郵件無法撤回。`)) return;
    setBusy('send'); setMessage(null);
    try {
      const { data } = await axios.post(endpoint, { ...payload, confirmRecipientCount: audience });
      setMessage({ error: false, text: `群發已開始，共 ${data.total} 位收件人。` });
      await refresh();
    } catch (error) {
      setMessage({ error: true, text: getError(error) });
      await refresh();
    } finally { setBusy(''); }
  };

  const cancel = async id => {
    if (!window.confirm('確定停止這次群發？已發出的郵件無法撤回。')) return;
    setBusy(`cancel-${id}`); setMessage(null);
    try {
      const { data } = await axios.post(`${endpoint}/${id}/cancel`);
      setMessage({ error: false, text: data.message });
      await refresh();
    } catch (error) { setMessage({ error: true, text: getError(error) }); }
    finally { setBusy(''); }
  };

  return <section className="email-broadcast-admin">
    <div className="admin-section-heading">
      <div><h2>群發郵件</h2><p>撰寫網站更新或功能說明，發送給所有未刪除的註冊用戶。</p></div>
    </div>
    <p className="email-broadcast-audience">目前收件人：<strong>{audience == null ? '載入中…' : `${audience} 位`}</strong>。每位用戶收到獨立郵件，不會看到其他人的地址。</p>
    {message && <div role="status" className={`admin-inline-message ${message.error ? 'is-error' : 'is-success'}`}>{message.text}</div>}
    <div className="email-broadcast-grid">
      <div className="email-broadcast-form">
        <label htmlFor="broadcast-subject">郵件標題</label>
        <input id="broadcast-subject" value={subject} maxLength={200} onChange={event => setSubject(event.target.value)} placeholder="例如：讀經計劃新增功能" />
        <label htmlFor="broadcast-body">郵件內容</label>
        <textarea id="broadcast-body" value={body} maxLength={20000} rows={12} onChange={event => setBody(event.target.value)} placeholder="在這裡撰寫更新內容，也可以貼上相關網址。" />
        <small>{body.length} / 20000 字。郵件會保留換行；不執行 HTML 程式碼。</small>
        <div className="email-broadcast-actions">
          <button type="button" onClick={sendTest} disabled={!valid || !!busy}>發送測試郵件給自己</button>
          <button type="button" className="primary" onClick={start} disabled={!valid || audience == null || audience === 0 || !!busy || active}>確認群發</button>
        </div>
        {active && <small>目前已有群發任務在處理，結束後才能開始下一次。</small>}
      </div>
      <div className="email-broadcast-preview">
        <h3>郵件預覽</h3>
        <strong>{subject.trim() || '郵件標題'}</strong>
        <div>{body.trim() || '郵件內容會在此顯示。'}</div>
      </div>
    </div>
    <div className="admin-subsection">
      <h3>最近群發記錄</h3>
      {history.length === 0 && <p className="admin-empty-state">暫無群發記錄。</p>}
      {history.map(item => <div className="email-broadcast-history" key={item.id}>
        <div><strong>{item.subject}</strong><small>{new Date(item.createdAt).toLocaleString()}</small></div>
        <p>共 {item.total} 位 · 已發 {item.sent} · 失敗 {item.failed} · 待核查 {item.unknown} · 待發 {item.pending} · 已停止 {item.canceled}</p>
        <div className="email-broadcast-history-footer">
          <span>{item.isCanceled ? '已停止' : item.completedAt ? '已結束' : '發送中'}</span>
          {!item.completedAt && !item.isCanceled && <button type="button" onClick={() => cancel(item.id)} disabled={!!busy}>停止未發郵件</button>}
        </div>
      </div>)}
      {history.some(item => item.unknown > 0) && <p className="email-broadcast-warning">「待核查」表示伺服器在發送時中斷，無法確認該郵件是否送達；系統不會自動重發，以免重複。</p>}
    </div>
  </section>;
}
